import { cn } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'

/**
 * Props for the {@link LinkAction} molecule.
 *
 * @property children - Visible label.
 * @property href - When supplied (and not disabled) the action renders as an anchor.
 * @property onPress - Invoked when the action renders as a button and is activated.
 * @property isDisabled - Renders a disabled button that neither navigates nor invokes.
 * @property className - Extra Tailwind classes merged onto the element.
 *
 * @example
 * ```tsx
 * <LinkAction href="/reports">Open</LinkAction>
 * <LinkAction onPress={refresh}>Refresh</LinkAction>
 * ```
 */
export interface LinkActionProps {
	children: ReactNode
	href?: string
	onPress?: () => void
	isDisabled?: boolean
	className?: string
}

const actionClasses =
	'rounded-ds-sm text-ds-base text-ds-primary underline-offset-2 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50'

/**
 * One inline action that is a link when it navigates and a button when it acts.
 *
 * @remarks
 * - **Correct element for the behaviour:** an `href` renders an `<a>` (so it can
 *   be opened in a new tab, copied, and crawled); without one it renders a
 *   `<button type="button">`. This is the distinction assistive technology and
 *   browsers rely on, so it is not merely cosmetic.
 * - **Disabled is always a button:** a disabled `<a>` is not a thing in HTML —
 *   an anchor with `href` stays navigable however it is styled. Rendering a
 *   disabled `<button>` is the only way the action genuinely does nothing.
 * - **Presentational only:** navigation and the press handler are the consumer's.
 * - **Styling:** every colour and radius class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/link-action/link-action.stories.tsx for visual examples
 */
export function LinkAction({ children, href, onPress, isDisabled, className }: LinkActionProps): ReactElement {
	if (isDisabled || !href) {
		return (
			<button type="button" disabled={isDisabled} onClick={onPress} className={cn(actionClasses, className)}>
				{children}
			</button>
		)
	}

	return (
		<a href={href} className={cn(actionClasses, className)}>
			{children}
		</a>
	)
}
